import type { Body, BodyUUID, Mesh, Quaternion, Vector3, WorldBody } from "../../types/scene";
import { generateUuid } from "../../utils/id";
import { logger } from "../../utils/logger";
import * as THREE from "three";

export type BodyRecord = Record<string, Body>;
export type JointRef = string | { uuid?: string; name?: string };
export type TendonJointMember = string | { joint?: string };
export type WorldBodyRef = WorldBody | BodyUUID | string;
export type RuntimeRegistryMap = Record<string, THREE.Object3D | undefined>;
export type BodyTreeNode = Omit<Body, "children"> & { children?: BodyTreeNode[] };
export type SceneGraphNode = THREE.Object3D & { isCSS2DObject?: boolean };

const ATTACHMENT_SITE_TOKEN_RE = /(^|_)attachment_site($|[_\d].*)/;

export const isAttachmentSiteName = (siteName: string | undefined | null): boolean =>
  ATTACHMENT_SITE_TOKEN_RE.test(String(siteName || ""));

// Normalize a quaternion so MuJoCo never sees a zero-length or non-finite rotation.
// Falls back to the identity quaternion [w,x,y,z] = [1,0,0,0].
export const sanitizeQuaternion = (q: Quaternion | undefined | null): Quaternion => {
  const w = Number(q?.[0]);
  const x = Number(q?.[1]);
  const y = Number(q?.[2]);
  const z = Number(q?.[3]);
  if (![w, x, y, z].every(Number.isFinite)) return [1, 0, 0, 0];
  const len = Math.hypot(w, x, y, z);
  if (len < 1e-8) return [1, 0, 0, 0];
  return [w / len, x / len, y / len, z / len];
};

// Sanitize a vec3 so MuJoCo never sees NaN/Infinity in body/geom positions.
export const sanitizeVector3 = (v: Vector3 | undefined | null): Vector3 => {
  const x = Number(v?.[0]);
  const y = Number(v?.[1]);
  const z = Number(v?.[2]);
  return [Number.isFinite(x) ? x : 0, Number.isFinite(y) ? y : 0, Number.isFinite(z) ? z : 0];
};

// Collect all mesh UUIDs referenced by the geoms of the given body UUIDs.
export function collectGeomMeshUuids(
  bodiesMap: BodyRecord,
  bodyUuids: Iterable<string>
): Set<string> {
  const meshUuids = new Set<string>();
  for (const uuid of bodyUuids) {
    const body = bodiesMap[uuid];
    (body?.geoms || []).forEach((geom) => {
      if (geom?.mesh) meshUuids.add(String(geom.mesh));
    });
  }
  return meshUuids;
}

// After body deletion, remove mesh assets that are no longer referenced by geoms.
export function pruneOrphanMeshes(
  draft: { meshes?: Record<string, Mesh>; bodies: BodyRecord },
  candidateMeshUuids: Set<string>
): void {
  if (!candidateMeshUuids.size || !draft.meshes) return;
  const meshes = draft.meshes;
  const survivingRefs = new Set<string>();
  Object.values(draft.bodies).forEach((body) => {
    (body?.geoms || []).forEach((geom) => {
      if (geom?.mesh) survivingRefs.add(String(geom.mesh));
    });
  });
  candidateMeshUuids.forEach((meshUuid) => {
    if (!survivingRefs.has(meshUuid)) {
      delete meshes[meshUuid];
    }
  });
}

// Helper to flatten body tree into Record and worldBodies array
// Converts children from Body[] to BodyUUID[]
export const flattenBodyTree = (
  nodes: BodyTreeNode[]
): { bodies: Record<string, Body>; worldBodies: WorldBody[] } => {
  const bodies: Record<string, Body> = {};
  const worldBodies: WorldBody[] = [];
  const processedUuids = new Set<string>(); // Track processed bodies to avoid duplicates

  const flatten = (node: BodyTreeNode): string | null => {
    if (!node) return null;

    // Ensure UUID exists
    if (!node.uuid) {
      node.uuid = generateUuid() as BodyUUID;
    }

    const uuid = node.uuid;

    // Skip if we've already processed this body (prevents infinite loops and duplicates)
    if (processedUuids.has(uuid)) {
      logger.warn(`[flattenBodyTree] Duplicate body UUID detected: ${uuid}, skipping`);
      return null;
    }

    processedUuids.add(uuid);
    const childUUIDs: string[] = [];

    // Recursively process children and collect their UUIDs
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        const childUUID = flatten(child);
        if (childUUID) {
          childUUIDs.push(childUUID);
        }
      });
    }

    // Store body with children as UUIDs only
    bodies[uuid] = {
      ...node,
      children: childUUIDs as BodyUUID[],
    };

    return uuid;
  };

  nodes.forEach((node) => {
    const uuid = flatten(node);
    if (uuid) {
      // Always generate "Object 1", "Object 2", etc. for robot-level world body labels
      const name = generateUniqueWorldBodyName(worldBodies);
      worldBodies.push({ uuid: uuid as BodyUUID, name });
    }
  });

  return { bodies, worldBodies };
};

/**
 * Generate a unique sequential name for a WorldBody like "Object 1", "Object 2", etc.
 * Checks existing worldBodies to find the next available number.
 */
export const generateUniqueWorldBodyName = (existingWorldBodies: WorldBody[] = []): string => {
  const existingNames = new Set(existingWorldBodies.map((wb) => wb.name));

  // Find the next available number
  let number = 1;
  let candidate = `Object ${number}`;
  while (existingNames.has(candidate)) {
    number += 1;
    candidate = `Object ${number}`;
  }

  return candidate;
};

export const normalizeBodiesRecord = (
  bodiesInput: Record<string, Body>
): { bodies: Record<string, Body>; keyToUuid: Record<string, BodyUUID> } => {
  const keyToUuid: Record<string, BodyUUID> = {};
  const normalized: Record<string, Body> = {};

  // Pass 1: ensure uuid exists; normalize record key = uuid
  for (const [inputKey, body] of Object.entries(bodiesInput || {})) {
    if (!body) continue;
    let uuid = (body.uuid || generateUuid()) as BodyUUID;
    if (normalized[uuid]) {
      const old = uuid;
      uuid = generateUuid() as BodyUUID;
      logger.warn("[setBodies] Duplicate body uuid detected while normalizing; reassigning", {
        inputKey,
        oldUuid: old,
        newUuid: uuid,
        name: body.name,
      });
    }
    keyToUuid[String(inputKey)] = uuid;
    if (body.uuid) keyToUuid[String(body.uuid)] = uuid;
    normalized[uuid] = { ...body, uuid } as Body;
  }

  // Pass 2: remap children arrays to normalized uuids
  for (const [uuid, body] of Object.entries(normalized)) {
    const children = Array.isArray(body.children) ? body.children : [];
    const remapped = children
      .map((c) => {
        const key = String(c ?? "");
        return keyToUuid[key] || c;
      })
      .filter(Boolean);
    normalized[uuid] = { ...body, children: remapped as BodyUUID[] };
  }

  return { bodies: normalized, keyToUuid };
};
